import type { CombinedError } from "@urql/core";
import { toast } from "@/components/ui/toast";
import { MeQuery, type MeUser } from "@/data-access/user/queries/me-queries";
import { client } from "@/lib/urqlProvider";
import { defaultUserInfo } from "@/types/user/userInfo";
import { mapMeToUserInfo } from "./auth.helpers";
import useAuth from "./auth.slice";
import { handleSessionExpired } from "./sessionTeardown";

const FETCH_ME_MAX_ATTEMPTS = 2;

/** User-facing copy when GQL `me` hydration fails outside login. */
export const profileLoadFailedMessage =
  "We couldn't load your profile. Please try again.";

/** Outcome of a GQL `me` hydration attempt. */
export type FetchMeResult = "success" | "session_expired" | "failed";

const isSessionExpiredError = (error: CombinedError | undefined): boolean =>
  Boolean(error?.message.includes("Session expired"));

const clearAuthTokens = () => {
  useAuth.setState({
    accessToken: null,
    refreshToken: null,
    loggedIn: false,
    user: defaultUserInfo,
    isEmployee: false,
  });
};

const applyMeToAuthState = (me: MeUser) => {
  const user = mapMeToUserInfo(me);
  useAuth.setState({
    user,
    isEmployee: user.email.endsWith("@handraise.com"),
  });
};

const queryMe = () =>
  client.query(MeQuery, {}, { requestPolicy: "network-only" }).toPromise();

/**
 * Hydrates auth user state from the GQL `me` query. Requires a valid access token.
 */
export const fetchMe = async (): Promise<FetchMeResult> => {
  if (!useAuth.getState().accessToken) {
    return "failed";
  }

  for (let attempt = 0; attempt < FETCH_ME_MAX_ATTEMPTS; attempt++) {
    try {
      const result = await queryMe();

      if (!result.error && result.data?.me) {
        applyMeToAuthState(result.data.me);
        return "success";
      }

      if (isSessionExpiredError(result.error)) {
        return "session_expired";
      }

      console.error("Failed to fetch me:", result.error);
    } catch (error) {
      if (error instanceof Error && error.message === "Session expired") {
        return "session_expired";
      }
      console.error("Error fetching me:", error);
    }
  }

  return "failed";
};

/**
 * Hydrates user state on authenticated route load when persist restored tokens
 * but not profile data.
 * @returns Whether the route may proceed (`true`) or should redirect to login (`false`).
 */
export const hydrateAuthUser = async (): Promise<boolean> => {
  const { user, accessToken } = useAuth.getState();
  if (!accessToken || user.id !== defaultUserInfo.id) {
    return true;
  }

  const result = await fetchMe();
  if (result === "success") {
    return true;
  }

  if (result === "session_expired") {
    handleSessionExpired();
    return false;
  }

  toast.error(profileLoadFailedMessage);
  clearAuthTokens();
  return false;
};
